import type { GetStaticPaths, GetStaticProps, NextPage } from 'next'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { BreadcrumbJsonLd, CollectionPageJsonLd, NextSeo } from 'next-seo'
import dynamic from 'next/dynamic'
import { IPost, PostOrderByInput } from 'src/types/hygraph'
import {
  HELP_CENTER_TOPICS,
  HygraphTopics,
  TOPIC_MAP,
  getPosts
} from 'src/utils/hygraph'
import loggerServer from 'src/utils/loggerServer'
import {
  HYGRAPH_PAGE_REVALIDATE_SECONDS,
  getPostDates,
  withCleanTitle
} from 'src/utils/postSeo'

import Footer from 'src/components/Footer'
import Category from 'src/components/Help/Category'
import {
  PINNED_HELP_SLUGS,
  sortPinnedFirst
} from 'src/components/Help/constants'
import NavBar from 'src/components/LandingNav'
import { toOgLocale } from 'src/utils/seo'

const GooglePopup = dynamic(() => import('src/components/Login/GooglePopup'), {
  ssr: false
})
const WhatsAppButton = dynamic(() => import('src/components/WhatsappButton'))

type IProps = {
  posts: IPost[]
  topic: HygraphTopics
  locale: string
}

const Page: NextPage<IProps> = ({ topic, posts, locale }) => {
  const { t } = useTranslation('common')
  if (!posts || !topic) return null
  const { path, title, label, description, metaImg } = TOPIC_MAP[topic]
  const url = `https://www.spotme.mx${path}`

  return (
    <>
      <NextSeo
        title={title}
        description={description ?? ''}
        canonical={url}
        openGraph={{
          type: 'website',
          url,
          title,
          description,
          locale: toOgLocale(locale),
          images: [{ url: metaImg, width: 1200, height: 630 }]
        }}
      />
      <CollectionPageJsonLd
        name={label}
        hasPart={posts.map((post) => ({
          about: post.title,
          author: { '@type': 'Person', givenName: post.author?.name },
          name: post.title,
          datePublished: getPostDates(post).datePublished,
          audience: { '@type': 'Audience', audienceType: 'Internet' },
          thumbnailUrl: post.coverImage?.url,
          image: post.coverImage?.url
        }))}
      />
      <BreadcrumbJsonLd
        itemListElements={[
          {
            position: 1,
            name: 'Ayuda',
            item: 'https://www.spotme.mx/acerca-de-spotme'
          },
          {
            position: 2,
            name: label,
            item: `https://www.spotme.mx${path}`
          }
        ]}
      />

      <main className="h-full">
        <NavBar />
        <Category posts={posts} topic={topic} />
        <Footer />

        <GooglePopup />
        <WhatsAppButton msg={t('HELP.WHATSAPP_FAB_MSG')} />
      </main>
    </>
  )
}

export default Page

type IParams = { topic: HygraphTopics }

export const getStaticPaths = (async ({ locales = [] }) => {
  const paths = locales
    .map((locale) =>
      HELP_CENTER_TOPICS.map((topic) => ({ params: { topic }, locale }))
    )
    .flat()
  return { paths, fallback: false }
}) satisfies GetStaticPaths

export const getStaticProps: GetStaticProps<IProps> = async (context) => {
  const { locale = 'es', params } = context
  const { topic } = params as IParams
  const translations = await serverSideTranslations(locale ?? 'es', [
    'common',
    'footer',
    'nav',
    'platform_stats',
    'auth'
  ])
  const response = await getPosts(1000, 0, PostOrderByInput.UpdatedAtDesc, {
    topic,
    locale,
    listOnly: true
  })
  const { posts } = response.data ?? {}
  if (!posts) {
    loggerServer.error('Error retrieving posts from hygraph.', {
      topic,
      response
    })
    throw new Error('Error retrieving posts from hygraph.')
  }
  const ordered = sortPinnedFirst(
    (posts as IPost[]).map(withCleanTitle),
    PINNED_HELP_SLUGS[topic]
  )
  return {
    props: { posts: ordered, topic, locale, ...translations },
    revalidate: HYGRAPH_PAGE_REVALIDATE_SECONDS
  }
}
